import { createFileRoute } from "@tanstack/react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import {
  rulesApi,
  type Adjustment,
  type IncreaseRule,
  type RuleListParams,
} from "@/services/api/hr";
import { useServerPagination } from "@/hooks/useServerPagination";

export const Route = createFileRoute("/portal/hr/rules/")({
  component: RulesPage,
});

function formatPercent(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? `${(n * 100).toFixed(2)}%` : value;
}

function ConfirmDeleteButton({
  onConfirm,
  disabled,
  testId,
}: {
  onConfirm: () => void;
  disabled?: boolean;
  testId: string;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" disabled={disabled} data-testid={testId}>
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
          <AlertDialogDescription>
            This affects payroll forecasts and can&apos;t be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid={`${testId}-confirm`}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RulesPage() {
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-slate-900">Rules</h1>
        <p className="mt-2 text-lg text-slate-600">
          Automated increases and one-off adjustments that drive payroll forecasts.
        </p>
      </div>
      <IncreaseRulesGrid orgId={orgId} />
      <AdjustmentsGrid orgId={orgId} />
    </div>
  );
}

interface Column<T> {
  header: string;
  sortKey?: string;
  cell: (row: T) => ReactNode;
  className?: string;
}

/** A standard server-paginated/search/sortable grid for a single rules list. */
function RuleGridCard<T extends { id: number }>({
  title,
  queryKeyBase,
  orgId,
  defaultSort,
  columns,
  listFn,
  deleteFn,
  deleteTestId,
  rowTestId,
  addButton,
}: {
  title: string;
  queryKeyBase: string;
  orgId: string;
  defaultSort: string;
  columns: Column<T>[];
  listFn: (
    token: string | null,
    params: RuleListParams,
  ) => Promise<{ items: T[]; total: number }>;
  deleteFn: (token: string | null, id: number) => Promise<void>;
  deleteTestId: (row: T) => string;
  rowTestId: (row: T) => string;
  addButton: (onCreated: () => void) => ReactNode;
}) {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    offset,
    searchTerm,
    setSearchTerm,
    getPaginationInfo,
  } = useServerPagination({ initialItemsPerPage: 25 });
  const [sortBy, setSortBy] = useState(defaultSort);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [prevOrg, setPrevOrg] = useState(orgId);
  if (prevOrg !== orgId) {
    setPrevOrg(orgId);
    setCurrentPage(1);
    setSearchTerm("");
  }

  const search = searchTerm.trim();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [queryKeyBase] });

  const list = useQuery({
    queryKey: [queryKeyBase, orgId, offset, itemsPerPage, search, sortBy, sortOrder],
    queryFn: () =>
      listFn(token, { offset, limit: itemsPerPage, search, sort: sortBy, order: sortOrder }),
    enabled: !!token && !!orgId,
    placeholderData: keepPreviousData,
  });

  const del = useMutation({
    mutationFn: (id: number) => deleteFn(token, id),
    onSuccess: () => {
      toast.success("Deleted");
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  const rows: T[] = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const { totalPages, startIndex, endIndex } = getPaginationInfo(total);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  useEffect(() => {
    if (list.data && totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [list.data, totalPages, currentPage, setCurrentPage]);

  function handleSort(key: string) {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  }

  const onSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  return (
    <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{title}</h2>
        {addButton(invalidate)}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchTerm}
          onChange={(e) => onSearch(e.target.value)}
          onClear={() => onSearch("")}
          placeholder={`Search ${title.toLowerCase()}…`}
          containerClassName="flex-1 min-w-[200px]"
          data-testid={`${queryKeyBase}-search`}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => list.refetch()}
          disabled={list.isFetching}
        >
          {list.isFetching ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {list.isError && (
        <p className="text-sm text-red-600">Failed to load {title.toLowerCase()}.</p>
      )}

      {list.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 && !list.isError ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {search ? `No ${title.toLowerCase()} match your search.` : `No ${title.toLowerCase()} yet.`}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) =>
                col.sortKey ? (
                  <SortableTableHead
                    key={col.header}
                    direction={sortBy === col.sortKey ? sortOrder : null}
                    onSort={() => handleSort(col.sortKey!)}
                  >
                    {col.header}
                  </SortableTableHead>
                ) : (
                  <SortableTableHead key={col.header} direction={null} onSort={() => {}} disabled>
                    {col.header}
                  </SortableTableHead>
                ),
              )}
              <SortableTableHead direction={null} onSort={() => {}} disabled className="text-right">
                Action
              </SortableTableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} data-testid={rowTestId(row)}>
                {columns.map((col) => (
                  <TableCell key={col.header} className={col.className}>
                    {col.cell(row)}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <ConfirmDeleteButton
                    onConfirm={() => del.mutate(row.id)}
                    disabled={del.isPending}
                    testId={deleteTestId(row)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {total > 0 && (
        <div className="mt-4 border-t pt-4">
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            totalCount={total}
            pageSize={itemsPerPage}
            pageSizeOptions={[10, 25, 50]}
            startIndex={startIndex}
            endIndex={endIndex}
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            onPageChange={setCurrentPage}
            onPageSizeChange={setItemsPerPage}
            onFirstPage={() => setCurrentPage(1)}
            onLastPage={() => setCurrentPage(totalPages)}
            onPrevPage={() => setCurrentPage(currentPage - 1)}
            onNextPage={() => setCurrentPage(currentPage + 1)}
          />
        </div>
      )}
    </div>
  );
}

function IncreaseRulesGrid({ orgId }: { orgId: string }) {
  return (
    <RuleGridCard<IncreaseRule>
      title="Increase rules"
      queryKeyBase="hr-increase-rules"
      orgId={orgId}
      defaultSort="effective_period"
      columns={[
        { header: "Effective", sortKey: "effective_period", cell: (r) => r.effective_period },
        { header: "Increase", sortKey: "percent", cell: (r) => formatPercent(r.percent) },
        {
          header: "Description",
          cell: (r) => r.description ?? "—",
          className: "text-sm text-muted-foreground",
        },
      ]}
      listFn={(token, params) => rulesApi.listIncreaseRules(token, params)}
      deleteFn={(token, id) => rulesApi.deleteIncreaseRule(token, id)}
      deleteTestId={(r) => `delete-increase-rule-${r.id}`}
      rowTestId={(r) => `increase-rule-${r.id}`}
      addButton={(onCreated) => <AddIncreaseRuleDialog onCreated={onCreated} />}
    />
  );
}

function AdjustmentsGrid({ orgId }: { orgId: string }) {
  return (
    <RuleGridCard<Adjustment>
      title="Adjustments"
      queryKeyBase="hr-adjustments"
      orgId={orgId}
      defaultSort="period"
      columns={[
        { header: "Period", sortKey: "period", cell: (a) => a.period },
        { header: "Amount", sortKey: "amount", cell: (a) => a.amount },
        {
          header: "Reason",
          cell: (a) => a.reason ?? "—",
          className: "text-sm text-muted-foreground",
        },
      ]}
      listFn={(token, params) => rulesApi.listAdjustments(token, params)}
      deleteFn={(token, id) => rulesApi.deleteAdjustment(token, id)}
      deleteTestId={(a) => `delete-adjustment-${a.id}`}
      rowTestId={(a) => `adjustment-${a.id}`}
      addButton={(onCreated) => <AddAdjustmentDialog onCreated={onCreated} />}
    />
  );
}

function AddIncreaseRuleDialog({ onCreated }: { onCreated: () => void }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState("");
  const [effectivePeriod, setEffectivePeriod] = useState("");
  const [description, setDescription] = useState("");

  const create = useMutation({
    mutationFn: () =>
      rulesApi.createIncreaseRule(token, {
        // The input is a percentage (5 = 5%); the API stores a decimal fraction.
        percent: (Number(rate) / 100).toString(),
        effective_period: effectivePeriod,
        description: description.trim() || null,
      }),
    onSuccess: () => {
      toast.success("Increase rule added");
      setOpen(false);
      setRate("");
      setEffectivePeriod("");
      setDescription("");
      onCreated();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Create failed"),
  });

  const rateNum = Number(rate);
  const rateValid = rate.trim().length > 0 && Number.isFinite(rateNum) && rateNum > 0;
  const canSubmit = rateValid && effectivePeriod.length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full" size="sm" data-testid="add-increase-rule">
          Add rule
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add increase rule</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rate">Increase (%)</Label>
            <Input
              id="rate"
              type="number"
              step="0.1"
              min="0"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="e.g. 5 for a 5% increase"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="effective">Effective period</Label>
            <Input
              id="effective"
              type="date"
              value={effectivePeriod}
              onChange={(e) => setEffectivePeriod(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rule-description">Description (optional)</Label>
            <Input
              id="rule-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => create.mutate()}
            disabled={!canSubmit || create.isPending}
            data-testid="create-increase-rule"
          >
            {create.isPending ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddAdjustmentDialog({ onCreated }: { onCreated: () => void }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const create = useMutation({
    mutationFn: () =>
      rulesApi.createAdjustment(token, {
        period,
        amount: amount.trim(),
        reason: reason.trim() || null,
      }),
    onSuccess: () => {
      toast.success("Adjustment added");
      setOpen(false);
      setPeriod("");
      setAmount("");
      setReason("");
      onCreated();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Create failed"),
  });

  const canSubmit = period.length > 0 && amount.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full" size="sm" data-testid="add-adjustment">
          Add adjustment
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add adjustment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="adj-period">Period</Label>
            <Input
              id="adj-period"
              type="date"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adj-amount">Amount</Label>
            <Input
              id="adj-amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="adj-reason">Reason (optional)</Label>
            <Input
              id="adj-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => create.mutate()}
            disabled={!canSubmit || create.isPending}
            data-testid="create-adjustment"
          >
            {create.isPending ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
